import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Calendar, Clock, Crown, Eye, Film, ImagePlus, Pencil, Star, Trash2 } from 'lucide-react';
import { Project } from '@/types/Project';
import { ProjectPermission, PERMISSION_LABELS, PERMISSION_LABELS_SIMPLE } from '@/types/Permissions';
import { formatDuration } from '@/lib/formatDuration';

export interface ProjectMemberInfo {
  user_id: string | null;
  permission: ProjectPermission;
  name: string;
}

export type ProjectWithMembers = Project & { members?: ProjectMemberInfo[] };

export function PermissionIcon({ permission, className }: { permission: ProjectPermission; className?: string }) {
  const cls = `w-3.5 h-3.5 ${className || ''}`;
  let icon: React.ReactNode;
  switch (permission) {
    case 'owner':
      icon = <Crown className={cls} />;
      break;
    case 'editor':
      icon = <Pencil className={cls} />;
      break;
    case 'contributor':
      icon = <ImagePlus className={cls} />;
      break;
    case 'viewer':
      icon = <Eye className={cls} />;
      break;
  }
  return <span className="inline-flex items-center justify-center w-4 h-4 shrink-0">{icon}</span>;
}

/** One `text-sm` stat line: fixed icon, then either the value or a placeholder. */
function Stat({ icon: Icon, children, barWidth }: {
  icon: typeof Clock;
  children?: React.ReactNode;
  /** Set instead of `children` to draw a placeholder bar of this Tailwind width. */
  barWidth?: string;
}) {
  return (
    <div className="flex items-center gap-2">
      <Icon className="w-4 h-4 shrink-0" />
      {barWidth ? (
        /* `h-5` on the wrapper, not the bar: `text-sm` has a 20px line box and
           the row's height comes from it. A bare 16px bar made every card 16px
           short, which the geometry guard caught as a 48px page-level miss. The
           bar itself stays 16px so it reads as text rather than a slab. */
        <div className="h-5 flex items-center">
          <div className={`h-4 rounded bg-white/20 ${barWidth}`} />
        </div>
      ) : children}
    </div>
  );
}

interface ProjectCardProps {
  /**
   * Omit while the list is loading and the card draws itself as a placeholder.
   *
   * This is the whole reason the loading state lives *inside* the card rather
   * than in a lookalike component next to it: the grid, the padding and the
   * four stat rows are written once, so the placeholder is exactly the height
   * of the real thing (206px desktop / 170px mobile) and cannot drift from it.
   * Only the leaves differ — a bar where the text goes.
   */
  project?: ProjectWithMembers;
  currentUserId?: string;
  onSelect?: (id: string) => void;
  onToggleFavorite?: (e: React.MouseEvent, id: string) => void;
  onDelete?: (id: string) => void;
  deleteDialog?: React.ReactNode;
}

export function ProjectCard({
  project,
  currentUserId,
  onSelect,
  onToggleFavorite,
  onDelete,
  deleteDialog,
}: ProjectCardProps) {
  const otherMembers = (project?.members || []).filter(
    (m) => m.user_id && m.user_id !== currentUserId
  );

  return (
    <Card
      className={`bg-white/10 backdrop-blur-md border-white/20 text-white relative overflow-hidden min-w-0 ${
        project ? 'hover:bg-white/15 transition-colors cursor-pointer' : ''
      }`}
      onClick={project && onSelect ? () => onSelect(project.id) : undefined}
      aria-busy={project ? undefined : true}
    >
      <CardHeader>
        <div className="flex items-center justify-between">
          {project ? (
            <CardTitle
              className="min-w-0 flex-1 pr-2 overflow-hidden whitespace-nowrap"
              style={{
                maskImage: 'linear-gradient(to right, black calc(100% - 2rem), transparent)',
                WebkitMaskImage: 'linear-gradient(to right, black calc(100% - 2rem), transparent)',
              }}
            >
              {project.name}
            </CardTitle>
          ) : (
            /* Matches `CardTitle`'s 24px line box so the header row is unchanged. */
            <div className="h-6 flex-1 mr-2 max-w-[14rem] rounded bg-white/20" />
          )}
          {project ? (
            <button
              onClick={(e) => onToggleFavorite?.(e, project.id)}
              className="shrink-0 p-1 rounded-full hover:bg-white/20 transition-colors"
              title={project.is_favorite ? 'Remove from favorites' : 'Add to favorites'}
            >
              <Star
                className={`w-5 h-5 transition-colors ${
                  project.is_favorite
                    ? 'text-yellow-300 fill-yellow-300'
                    : 'text-white/40 hover:text-white/70'
                }`}
              />
            </button>
          ) : (
            /* The star button is `p-1` around a 20px icon = 28px, and it — not
               the title — sets the header row's height. */
            <div className="shrink-0 h-7 w-7 rounded-full bg-white/20" />
          )}
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm text-white/60">
          {/* Left column: stats */}
          <div className="space-y-2 min-w-0">
            <Stat icon={Clock} barWidth={project ? undefined : 'w-28'}>
              {project && `${formatDuration(project.total_duration || 0)} Runtime`}
            </Stat>
            <Stat icon={Film} barWidth={project ? undefined : 'w-16'}>
              {project && `${project.video_count ?? 0} Clips`}
            </Stat>
            <Stat icon={Calendar} barWidth={project ? undefined : 'w-32'}>
              {project && `Created ${new Date(project.created_at).toLocaleDateString()}`}
            </Stat>
            <Stat icon={Calendar} barWidth={project ? undefined : 'w-32'}>
              {project && `Modified ${new Date(project.updated_at).toLocaleDateString()}`}
            </Stat>
          </div>

          {/* Right column: permission + shared-with */}
          <div className="flex flex-col items-end gap-1 min-w-0">
            {!project && <div className="h-5 w-20 rounded-full bg-white/20" />}
            {project?.user_permission && (
              <Badge
                variant="secondary"
                className="text-xs bg-white/20 text-white border-white/30 gap-1"
                title={PERMISSION_LABELS[project.user_permission]}
              >
                {PERMISSION_LABELS_SIMPLE[project.user_permission]}
                <PermissionIcon permission={project.user_permission} className="text-white/80" />
              </Badge>
            )}
            {otherMembers.length > 0 && (
              <div className="flex flex-col items-end gap-1 max-w-full">
                {otherMembers.map((m) => (
                  <div
                    key={m.user_id || m.name}
                    className="flex items-center gap-1 max-w-full rounded-full bg-white/15 border border-white/20 px-2 py-0.5 text-xs text-white"
                    title={`${m.name} — ${PERMISSION_LABELS[m.permission]}`}
                  >
                    <span className="truncate max-w-[8rem]">{m.name}</span>
                    <PermissionIcon permission={m.permission} className="text-white/80" />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </CardContent>

      {project && onDelete && (
        <>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDelete(project.id);
            }}
            className="absolute bottom-3 right-3 p-2 rounded-full bg-red-500/80 hover:bg-red-600 text-white transition-colors z-10"
            title="Delete project"
          >
            <Trash2 className="w-4 h-4" />
          </button>
          {deleteDialog}
        </>
      )}
    </Card>
  );
}
